import Settings from '../domain/Settings';
import Slot from '../domain/Slot';
import * as perf from '../utils/performance';
import breakpoints from '../services/breakpoints';
import * as privacyService from '../services/privacyService';
import {hasUserProfilingConsent} from '../services/privacyService';
import sizeMappings, {CustomBreakpointMapping, SizeMapping, Sizes} from '../services/sizeMappings';
import * as targeting from '../services/targeting';
import {getDMOI, getRandomizedDMOI} from '../services/userService';
import {getDSASettings} from '../utils/dsa';
import {SlotStateError} from '../domain/SlotStateError';
import {getArrayBatched} from '../utils/batch-util';
import {AdServer} from '../domain/AdServer';
import {getForcedCreativeId} from '../utils/debug-overrides';
import {isObject} from '../utils/object-util';
import logger from '../utils/logger';

enum XandrErrorType {
	AdBadRequest = 'adBadRequest',
	AdRequestFailure = 'adRequestFailure'
}

/* Prep apntag */
window.apntag = window.apntag || {} as Xandr.ApnTag;
window.apntag.anq = window.apntag.anq || [];

let eventsHooked = false,
	enabled = false;

const queueReady = new Promise<void>((resolve) => {
	window.apntag.anq.push(() => {
		resolve();
	});
});

function _hookEvents() {
	if (eventsHooked) {
		return;
	}

	window.apntag.onEvent('adError', (error, adObj) => {
		logger.error('Something went wrong in an ad creative for the ad ', adObj, ' | Error: ', error);
	});
	eventsHooked = true;
}

export async function init() {
	enabled = privacyService.hasXandrConsent() || !!Settings.getInstance().features?.xandrSimpleAds;

	if (!enabled) {
		return;
	}

	perf.mark('xandr - apntag.init - start');

	perf.mark('xandr - apntag.cmd wait - start');
	await queueReady;
	perf.mark('xandr - apntag.cmd wait - end');

	window.apntag.clearRequest();

	window.apntag.setPageOpts(_getPageOptionsFromSettings());
	setUserPageOptions();
	_hookEvents();

	perf.mark('xandr - apntag.init - end');
}

export function defineSlot(slot: Slot) {
	window.apntag.defineTag(_getTagValuesFromSlot(slot));
	addSlotListeners(slot);
	slot.setDefined(true, AdServer.Xandr);
}

function _formatSizeMappingForXandr(sizeMap: SizeMapping): Xandr.SizeMap[] {
	const sizeMaps: Xandr.SizeMap[] = Object.entries(breakpoints.getBreakpoints()).map(([bpName, bpBegin]) => ({
		'minWidth': bpBegin,
		'sizes': [...sizeMap[bpName] as Sizes ?? []]
	}));

	if (sizeMap.customBreakpointMapping) {
		return [...sizeMaps, ...(sizeMap.customBreakpointMapping as CustomBreakpointMapping)];
	}

	return sizeMaps;
}

const DEFAULT_SLOT_LOAD_BATCH_COUNT = 6;

function getBatchCount(): number {
	let batchCount = Settings.getInstance().loadSlotBatchCount;

	if (typeof batchCount === 'object') {
		batchCount = batchCount[breakpoints.getCurrentBreakpoint()];
	}

	if (typeof batchCount !== 'number') {
		return DEFAULT_SLOT_LOAD_BATCH_COUNT;
	}

	return batchCount;
}

type SlotsByMemberId = {
	main: Slot[];
	overridden: Array<{
		slots: Slot[]
		memberId: number
		keywords: string[]
	}>
};

function _sortByMemberIdAndKeywords(definedSlots: Slot[]): SlotsByMemberId {
	return definedSlots.reduce((agg, slot) => {
		if (!slot.memberId) {
			agg.main.push(slot);
			return agg;
		}

		const slotDifferentSeatKeywords = slot.differentSeatKeywords ?? [],
			matchedGroup = agg.overridden.find(group =>
				group.memberId === slot.memberId
				&& group.keywords.length === slotDifferentSeatKeywords.length
				&& group.keywords.every((keyword) => slotDifferentSeatKeywords.includes(keyword)));

		if (!matchedGroup) {
			agg.overridden.push({
				slots: [slot],
				memberId: slot.memberId,
				keywords: slotDifferentSeatKeywords
			});
		} else {
			matchedGroup.slots.push(slot);
		}

		return agg;
	}, {
		main: [],
		overridden: []
	} as SlotsByMemberId);
}

export async function loadSlots(slots: Slot[]) {
	const batchCount = getBatchCount(),
		definedSlots = slots.filter((slot) => slot.getState(AdServer.Xandr).defined),
		{
			main: defaultMemberIdSlots,
			overridden: memberIdSlots
		} = _sortByMemberIdAndKeywords(definedSlots);

	getArrayBatched(defaultMemberIdSlots, batchCount)
		.map((batch) => loadSlotsDefault(batch));

	memberIdSlots.forEach(({slots: slotsGroup, keywords}) => {
		getArrayBatched(slotsGroup, batchCount).forEach((batchedSlots) => {
			loadSlotsWithLimitedTargeting(batchedSlots as Slot[], keywords);
		});
	});
}

function loadSlotsDefault(slots: Slot[]) {
	window.apntag.loadTags(slots.map((s) => s.domId));
}

export function loadSlotsWithLimitedTargeting(slots: Slot[] = [], keywordsToKeep: string[] = []) {
	if (slots.length === 0) {
		return;
	}

	const {
			user,
			keywords: originalKeywords
		} = window.apntag.requests,
		keywords = Object.fromEntries(Object.entries(originalKeywords).filter(([key]) => keywordsToKeep.includes(key)));

	// Clear properties
	window.apntag.setPageOpts({
		keywords,
		user: {}
	});

	window.apntag.loadTags(slots.map((s) => s.domId));

	// Reset, breaks because of consent
	window.apntag.setPageOpts({
		keywords: originalKeywords,
		user
	});
}

export function renderSlot(slot: Slot) {
	resetSlot(slot);
	window.apntag.showTag(slot.domId);
	slot.setShowTagCalled(true, AdServer.Xandr);
}

export function resetSlot(slot: Slot) {
	window.apntag.modifyTag(slot.domId, _getTagValuesFromSlot(slot));
}

export function applySizeMapping(slot: Slot, sizeMapping: SizeMapping) {
	window.apntag.modifyTag(slot.domId, {
		sizeMapping: _formatSizeMappingForXandr(sizeMapping)
	});
}

export function refreshSlots(slots: Slot[] = []) {
	window.apntag.refresh(slots.map((s) => s.domId));
}

function _getPageOptionsFromSettings() {
	const {
		memberId,
		enableSafeFrame,
		disablePSA,
		auctionTimeout
	} = Settings.getInstance();

	return {
		'member': memberId,
		auctionTimeout,
		'enableSafeFrame': enableSafeFrame,
		'enableMediationEvents': true,
		'loadOutstreamUpgradeUrl': true,
		'disablePsa': disablePSA,
		'keywords': targeting.getPageTargeting(),
		'dsa': getDSASettings()
	};
}

export function setUserPageOptions(userIds: AnyObject[] = []) {
	if (!hasUserProfilingConsent()) {
		return;
	}

	const userPageOption: AnyObject = {},
		settings = Settings.getInstance(),
		userId = getDMOI(),
		extUid = settings.user ?? userId;

	if (extUid) {
		userPageOption.externalUid = extUid;
	}

	if (settings.features?.dmoi) {
		const dmoiSource = settings.dmoi?.source;

		if (dmoiSource && userId) {
			userIds.push({
				'source': dmoiSource,
				'id': userId,
				'type': 'DMOI'
			});
		}

		const rdmoiSource = settings.dmoi?.rdmoiSource,
			rUid = getRandomizedDMOI();

		if (rdmoiSource && rUid) {
			userIds.push({
				'source': rdmoiSource,
				'id': rUid,
				'type': 'RDMOI'
			});
		}
	}

	if (userIds.length > 0) {
		userPageOption.userIds = userIds;
	}

	if (Object.keys(userPageOption).length > 0) {
		window.apntag.setPageOpts({'user': userPageOption});
	}
}

export function updatePageTargeting() {
	window.apntag.setPageOpts({
		'keywords': targeting.getPageTargeting()
	});
}

function _getTagValuesFromSlot(slot: Slot): Xandr.Tag {
	const slotSizeMapping = sizeMappings.getSizeMapping(slot.sizeMapping),
		tag: Xandr.Tag = {
			'targetId': slot.domId,
			'invCode': slot.getPlacement(AdServer.Xandr),
			'sizeMapping': _formatSizeMappingForXandr(slotSizeMapping),
			'keywords': targeting.getSlotTargeting(slot) as Record<string, string>
		},
		forceCreativeId = getForcedCreativeId(slot.name, AdServer.Xandr);

	if (forceCreativeId) {
		tag.forceCreativeId = parseInt(forceCreativeId, 10);
	}

	if (slot.memberId) {
		tag.member = slot.memberId;
	}

	if (Settings.getInstance().features.gpid) {
		tag.globalPlacementId = isObject(slot.gpid) ? slot.gpid[breakpoints.getCurrentBreakpoint()] : slot.gpid;
	}

	return tag;
}

export function resizeSlot(slot: Slot, newSize: [number, number]) {
	window.apntag.resizeAd(slot.domId, newSize);
}

function addSlotListeners(slot: Slot) {
	addRenderFinishListener(slot);
	addAvailableListeners(slot);
	addRequestedListeners(slot);
	addMediationListeners(slot);
}

export function addRenderFinishListener(slot: Slot) {
	window.apntag.onEvent('adLoaded', slot.domId, (adObj: AnyObject) => _onRenderFinish(slot, adObj as Xandr.AdObject));
}

function _onRenderFinish(slot: Slot, adObject: Xandr.AdObject) {
	slot.setAdData(AdServer.Xandr, adObject);
	slot.setRendered(true, AdServer.Xandr);
}

export function addAvailableListeners(slot: Slot) {
	window.apntag.onEvent('adAvailable', slot.domId, (adObj: AnyObject) => _onAdAvailable(slot, {
		'nobid': false,
		...adObj
	} as Xandr.AdObject));

	// AdObj can be missing with mediation, so we at least provide for isEmpty
	window.apntag.onEvent('adNoBid', slot.domId, (adObj: AnyObject) => _onAdAvailable(slot, {
		'nobid': true,
		...adObj ?? {}
	} as Xandr.AdObject));
}

function _onAdAvailable(slot: Slot, adObject: Xandr.AdObject) {
	slot.setAdData(AdServer.Xandr, adObject);
	slot.setReceived(true, AdServer.Xandr);
}

export function addRequestedListeners(slot: Slot) {
	window.apntag.onEvent('adRequested', slot.domId, () => _onAdRequestedEvent(slot));
	window.apntag.onEvent('adBadRequest', slot.domId, (...params) => _onAdRequestFailureEvent(slot, XandrErrorType.AdBadRequest, ...params));
	window.apntag.onEvent('adRequestFailure', slot.domId, (...params) => _onAdRequestFailureEvent(slot, XandrErrorType.AdRequestFailure, ...params));
}

function _onAdRequestedEvent(slot: Slot) {
	slot.setRequested(true, AdServer.Xandr);
}

function _onAdRequestFailureEvent(slot: Slot, error: XandrErrorType, ...params: Array<unknown>) {
	const errorType = _xandrErrorToSlotError(error);

	slot.setAdData(AdServer.Xandr, {nobid: true} as Xandr.AdObject);
	slot.setReceived(true, AdServer.Xandr);
	slot.setError(errorType, AdServer.Xandr);

	logger.error('Could not load slot', slot, '| Error type:', errorType, '| Error data:', ...params);
}

function _xandrErrorToSlotError(xandrError: XandrErrorType): SlotStateError {
	switch (xandrError) {
		case XandrErrorType.AdRequestFailure:
			return SlotStateError.REQUEST_FAILURE;

		case XandrErrorType.AdBadRequest:
			return SlotStateError.BAD_REQUEST;
	}
}

export function addMediationListeners(slot: Slot) {
	window.apntag.onEvent('adLoadedMediated', slot.domId, () => _onMediationEvent(slot, false));
	window.apntag.onEvent('adNoBidMediated', slot.domId, () => _onMediationEvent(slot, true));
}

function _onMediationEvent(slot: Slot, empty: boolean) {
	slot.setMediated(true, empty);
}

export function removeSlot(slot: Slot) {
	removeRequestedListeners(slot);
	removeRenderFinishListener(slot);
	removeAvailableListeners(slot);
	removeMediationListeners(slot);
}

function removeRequestedListeners(slot: Slot) {
	window.apntag.offEvent('adRequested', slot.domId);
	window.apntag.offEvent('adBadRequest', slot.domId);
	window.apntag.offEvent('adRequestFailure', slot.domId);
}

function removeRenderFinishListener(slot: Slot) {
	window.apntag.offEvent('adLoaded', slot.domId);
}

function removeAvailableListeners(slot: Slot) {
	window.apntag.offEvent('adAvailable', slot.domId);
	window.apntag.offEvent('adNoBid', slot.domId);
}

function removeMediationListeners(slot: Slot) {
	window.apntag.offEvent('adLoadedMediated', slot.domId);
	window.apntag.offEvent('adNoBidMediated', slot.domId);
}

export function isEnabled() {
	return enabled;
}
